import * as React from "react";
import { useEffect, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Grid,
  Stack,
  Avatar,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TablePagination from "@mui/material/TablePagination";
import { showToast } from "services/utils/Status";
import "react-toastify/dist/ReactToastify.css";
import { Loader } from "components/Loader";
import { useSelector } from "react-redux";
import { themes, scrollTop, optionalAvathar } from "services/constants";
import sessionHandling from "services/utils/notificationUtils";
import { tableRowSx } from "services/constants";
import { weekoffRejectHeader } from "../Headers";
import { InfoMessage } from "components/CustomComponents/CustomButton";

export const Approved = ({ startDate, endDate }) => {
  const { domain, token, userDetails, globalSearchValue, topbarCompanyId } =
    useSelector((state) => state.tokenReducer);
  const [loading, setLoading] = useState(false);
  const { departmentId } = useSelector((state) => state.userReducer);
  const [expandedRow, setExpandedRow] = React.useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filteredData, setFilteredData] = useState([]);
  const [rejectOt, setRejectOt] = useState([]);
  const containerRef = useRef(null);

  const handleRowClick = (rowId) => {
    if (expandedRow === rowId) {
      setExpandedRow(null);
    } else {
      setExpandedRow(rowId);
    }
  };

  useEffect(() => {
    const fetchReject = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${domain}week-off-approvals/?user_id=${userDetails.id}&start_date=${startDate}&end_date=${endDate}&status=Rejected&department_id=${departmentId}&company_id=${topbarCompanyId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          }
        );
        const res = await response.json();
        if (response.ok) {
          setFilteredData(res);
          setRejectOt(res);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error(res.error);
        }
      } catch (error) {
        showToast("error", error.message);
      }
      setLoading(false);
    };
    fetchReject();
  }, [
    departmentId,
    domain,
    endDate,
    startDate,
    token,
    topbarCompanyId,
    userDetails.id,
  ]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const searchFields = [
      "user_name",
      "date",
      "shift",
      "check_in_datetime",
      "check_out_datetime",
      "approvedby",
      "createdat",
    ];
    const filteredResults = rejectOt.filter((item) =>
      searchFields.some((key) =>
        item[key]
          ?.toString()
          .toLowerCase()
          .includes(globalSearchValue.toString().toLowerCase())
      )
    );
    setFilteredData(filteredResults);
    setPage(0);
  }, [globalSearchValue, rejectOt]);

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  return loading ? (
    <Loader info="Loading..." />
  ) : (
    <React.Fragment>
      <Grid container>
        <TableContainer
          ref={containerRef}
          sx={{
            marginTop: 3,
            marginLeft: "auto",
            marginRight: "auto",
            overflow: "auto",
            ml: 2,
            mr: 2,
          }}
        >
          <Table className="border">
            <TableHead
              sx={{
                backgroundColor: themes.primary,
                position: "sticky",
                top: -1,
                zIndex: 10,
              }}
            >
              <TableRow sx={tableRowSx}>
                {weekoffRejectHeader.map((header) => (
                  <TableCell
                    sx={{ color: themes.headLine, fontWeight: "bold" }}
                  >
                    {header.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {filteredData.length === 0 ? (
              <InfoMessage dataLength={rejectOt} />
            ) : filteredData.length > 0 ? (
              <TableBody>
                {filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <>
                      <TableRow sx={tableRowSx}>
                        {weekoffRejectHeader.map((header) => (
                          <TableCell sx={{ color: themes.headLine }}>
                            {header.key === "arrow" ? (
                              <>
                                {expandedRow === item.id ? (
                                  <IconButton
                                    onClick={() => {
                                      setExpandedRow(null);
                                    }}
                                  >
                                    <KeyboardArrowDownIcon
                                      style={{
                                        color: "black",
                                        fontWeight: "bold",
                                      }}
                                    />
                                  </IconButton>
                                ) : (
                                  <IconButton
                                    onClick={() => handleRowClick(item.id)}
                                  >
                                    <KeyboardArrowRightIcon
                                      style={{
                                        color: "black",
                                        fontWeight: "bold",
                                      }}
                                    />
                                  </IconButton>
                                )}
                              </>
                            ) : header.name === "Employee name" ? (
                              <Stack direction="Row">
                                <Avatar
                                  sx={{ width: 23, height: 23 }}
                                  alt={item?.user_name?.charAt(0).toUpperCase()}
                                  src={item?.image_url ?? optionalAvathar}
                                />
                                <Typography
                                  sx={{
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                    paddingLeft: 1,
                                  }}
                                >
                                  {item?.user_name ? item?.user_name : "N/A"}
                                </Typography>
                              </Stack>
                            ) : item[header.key] ? (
                              item[header.key]
                            ) : (
                              "N/A"
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                      {expandedRow === item.id && (
                        <TableRow sx={tableRowSx}>
                          <TableCell colSpan={9}>
                            <TableContainer
                              sx={{
                                marginTop: 0.5,
                                marginLeft: "auto",
                                marginRight: "auto",
                              }}
                              className="w-96 border rounded"
                            >
                              <Table className="border">
                                <Typography
                                  variant="body1"
                                  gutterBottom
                                  sx={{ marginLeft: 1, marginTop: 0.5 }}
                                >
                                  <strong>Reason for Rejection :</strong>{" "}
                                  <Typography
                                    fontSize={15}
                                    color="textSecondary"
                                    sx={{ marginLeft: 13 }}
                                  >
                                    {item.reason}
                                  </Typography>
                                </Typography>
                              </Table>
                            </TableContainer>
                            {item.title === "Leave" ? null : (
                              <TableContainer
                                sx={{
                                  marginTop: 0.5,
                                  marginLeft: "auto",
                                  marginRight: "auto",
                                  width: "100%px",
                                }}
                                className="w-96 border rounded"
                              >
                                <Table className="border">
                                  <Typography
                                    variant="body1"
                                    gutterBottom
                                    sx={{ marginLeft: 1, marginTop: 0.5 }}
                                  >
                                    <strong>
                                      Time Period : {item.check_in_datetime} -{" "}
                                      {item.check_out_datetime}
                                    </strong>
                                  </Typography>
                                </Table>
                              </TableContainer>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
              </TableBody>
            ) : null}
          </Table>
          {filteredData.length > 25 && (
            <TablePagination
              className="custom-pagination"
              rowsPerPageOptions={[25, 50, 75, 100]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </TableContainer>
      </Grid>
    </React.Fragment>
  );
};
